import { useEffect, useState } from "react";
import { useAtom, useSetAtom } from "jotai";
import { useTranslation } from "react-i18next";
import { Input } from "components/common/input/Input";
import { RadioGroup, RadioItem } from "components/common/radioGroup/RadioGroup";
import { Button } from "wiz-front-shared/components/button/Button";
import { CheckBox } from "wiz-front-shared/components/checkBox/CheckBox";
import { pushStepAtom, signUpDataAtom, SignUpData } from "store/signUpStore";
import { useSignupPolicy } from "hooks/useSignupPolicy";
import "components/identity/Identity.css";

type Relationship = SignUpData["guardianRelationship"];

/**
 * Step11: 본인 정보 확인 (가입 연령 정책 적용 서비스)
 *
 * - 본인 이름 + "입력한 정보가 본인의 실제 정보임을 확인합니다" 체크 (전 가입자)
 * - 법정대리인 동의 대상이면 보호자 이름·아동과의 관계 추가 → 다음: 보호자 휴대폰(Step12)
 * - 아니면 다음: 인증 단계
 */
export const SignUpStep11 = () => {
    const { t } = useTranslation();
    const pushStep = useSetAtom(pushStepAtom);
    const [signUpData, setSignUpData] = useAtom(signUpDataAtom);
    const { routeToAuth } = useSignupPolicy();
    const isGuardian = signUpData.guardianRequired;

    const [name, setName] = useState(signUpData.name || "");
    const [guardianName, setGuardianName] = useState(signUpData.guardianName || "");
    const [relationship, setRelationship] = useState<Relationship>(signUpData.guardianRelationship || "");
    const [confirmed, setConfirmed] = useState(signUpData.infoConfirmed);
    const [isBtnDisabled, setIsBtnDisabled] = useState(true);

    const relationshipItems: RadioItem[] = [
        { label: t("signUp.step11.relationFather"), value: "FATHER" },
        { label: t("signUp.step11.relationMother"), value: "MOTHER" },
        { label: t("signUp.step11.relationLegalGuardian"), value: "LEGAL_GUARDIAN" },
        { label: t("signUp.step11.relationOther"), value: "OTHER" },
    ];

    useEffect(() => {
        const selfOk = confirmed && (!isGuardian || name.trim().length > 0);
        const guardianOk = !isGuardian || (guardianName.trim().length > 0 && !!relationship);
        setIsBtnDisabled(!(selfOk && guardianOk));
    }, [name, guardianName, relationship, confirmed, isGuardian]);

    const handleNext = () => {
        if (isBtnDisabled) return;
        const updated: SignUpData = {
            ...signUpData,
            name: isGuardian ? name.trim() : signUpData.name,
            infoConfirmed: true,
            guardianName: isGuardian ? guardianName.trim() : "",
            guardianRelationship: isGuardian ? relationship : "",
        };
        setSignUpData(updated);
        // 보호자 번호 입력(Step12) — 단, 본인인증(다날)을 쓰는 국내 보호자는 기존 보호자 다날 단계에서 번호를 받는다
        if (isGuardian && !(updated.idvEnabled && updated.countryCd === "KR")) {
            pushStep(12);
            return;
        }
        routeToAuth(updated);
    };

    return (
        <div className="identity-form">
            <span className="identity-title">
                {t(isGuardian ? "signUp.step11.titleGuardian" : "signUp.step11.title")}
            </span>
            <span className="identity-info">
                {isGuardian
                    ? t("signUp.step11.infoGuardian", { age: signUpData.consentAge })
                    : t("signUp.step11.info")}
            </span>

            {isGuardian && (
                <>
                    <Input
                        id="signup-name"
                        inputVal={name}
                        setInputVal={setName}
                        title={t("signUp.name")}
                        placeholder={t("signUp.namePlaceHolder")}
                        maxLength={50}
                        isRequired
                    />
                    <Input
                        id="signup-guardian-name"
                        inputVal={guardianName}
                        setInputVal={setGuardianName}
                        title={t("signUp.step11.guardianName")}
                        placeholder={t("signUp.step11.guardianNamePlaceHolder")}
                        maxLength={50}
                        isRequired
                    />
                    <RadioGroup
                        name="signup-guardian-relationship"
                        items={relationshipItems}
                        value={relationship}
                        onChange={(v) => setRelationship(v as Relationship)}
                        title={t("signUp.step11.relationship")}
                        isRequired
                    />
                </>
            )}

            <div className="identity-parent-consent-area show">
                <CheckBox
                    title={t("signUp.step11.infoConfirm")}
                    id="signup-info-confirm"
                    checked={confirmed}
                    setChecked={setConfirmed}
                    required
                />
            </div>

            <div className="identity-bottom-btn">
                <Button
                    text={t("signUp.btnNext")}
                    onPress={handleNext}
                    disabled={isBtnDisabled}
                />
            </div>
        </div>
    );
};
